import { cn } from "@/lib/utils";
import { maskitoDateOptionsGenerator } from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import { parse } from "date-fns";
import { format } from "date-fns-tz";
import { de } from "date-fns/locale";
import { CalendarIcon, X } from "lucide-react";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Month/Day mask
const mask = maskitoDateOptionsGenerator({
  mode: "dd/mm",
  separator: ".",
});

type Props = {
  name: string;
  label?: string | React.ReactNode;
  disabled?: boolean;
  description?: string;
  readOnly?: boolean;
  className?: string;
  clearButton?: boolean;
};

const generateStringDate = (date: Date | undefined | null) => {
  if (!date || date.toString() === "Invalid Date") {
    return "";
  }
  return format(date, "dd.MM");
};

export const MonthDayPicker = ({
  label,
  name,
  className,
  disabled = false,
  description,
  readOnly = false,
  clearButton = false,
}: Props) => {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <MonthDayPickerContent
          field={field}
          label={label}
          className={className}
          disabled={disabled}
          description={description}
          readOnly={readOnly}
          clearButton={clearButton}
        />
      )}
    />
  );
};

const MonthDayPickerContent = ({
  field,
  label,
  className,
  disabled,
  description,
  readOnly,
  clearButton,
}: {
  field: any;
  label?: string | React.ReactNode;
  className?: string;
  disabled: boolean;
  description?: string;
  readOnly: boolean;
  clearButton?: boolean;
}) => {
  const { error } = useFormField();
  const form = useFormContext();
  const [value, setValue] = React.useState<Date | undefined>(field.value);
  const [inputValue, setInputValue] = React.useState<string>(
    generateStringDate(field.value),
  );
  const maskedInputRef = useMaskito({ options: mask });

  useEffect(() => {
    setValue(field.value);
    setInputValue(generateStringDate(field.value));
  }, [field.value]);

  const handleOnChange = (date: Date | undefined) => {
    if (!date) {
      field.onChange(null);
    } else if (date.toString() === "Invalid Date") {
      field.onChange(value ?? null);
    } else {
      // Always use 2024 as the year (leap year to handle Feb 29)
      const newDate = new Date(2024, date.getMonth(), date.getDate());
      field.onChange(newDate);
    }
  };

  const handleClear = () => {
    setValue(undefined);
    setInputValue("");
    field.onChange(null);
    form.setValue(field.name, null, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const datePickerContent = (
    <Popover
      modal
      onOpenChange={(open) => {
        if (!open) {
          field.onBlur();
        }
      }}
    >
      <div
        className={cn(
          "relative rounded-md border border-input bg-background focus:outline-none focus-visible:outline-muted-foreground",
          className,
        )}
      >
        <Input
          type="string"
          value={inputValue}
          ref={maskedInputRef}
          className={cn("border-0 bg-transparent")}
          placeholder="TT.MM"
          readOnly={readOnly}
          disabled={disabled}
          onInput={(e: any) => {
            const value = e.target.value;
            setInputValue(value);
            if (value.length === 5) {
              const parsedDate = parse(
                value + ".2024",
                "dd.MM.yyyy",
                new Date(),
              );
              setValue(parsedDate);
              handleOnChange(parsedDate);
            } else if (value.length === 0) {
              handleOnChange(undefined);
            }
          }}
        />
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            tabIndex={-1}
            disabled={disabled}
            className={cn(
              "font-normal absolute right-0 translate-y-[-50%] top-[50%] rounded-l-none border-0 bg-transparent h-9 mr-0.5 text-muted-foreground",
            )}
          >
            <CalendarIcon className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0 z-[2000]">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(selectedDate) => {
            if (!selectedDate) return;
            setValue(selectedDate);
            handleOnChange(selectedDate);
            const stringDate = format(selectedDate, "dd.MM");
            setInputValue(stringDate);
          }}
          defaultMonth={value}
          locale={de}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );

  return (
    <FormItem>
      {label && <FormLabel>{label}</FormLabel>}
      <FormControl>
        {clearButton ? (
          <div className="flex items-end gap-2">
            <div className="flex-1">{datePickerContent}</div>
            {value && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-10 w-10 p-0 text-muted-foreground hover:text-foreground"
                onClick={handleClear}
                disabled={disabled || readOnly}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          datePickerContent
        )}
      </FormControl>
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage />
    </FormItem>
  );
};
